import {useCallback, useEffect, useRef, useState} from 'react'
import {APP_VERSION} from 'src/appVersion'

/**
 * Compares the version compiled into this bundle (`APP_VERSION`) against the
 * version the server is currently serving (`/version.json`, written at build
 * time by `scripts/gen-version.js`).
 *
 * A mismatch means the browser is still running an outdated bundle, so the
 * user needs a hard refresh.
 *
 * `version.json` is fetched with a cache-busting query param and
 * `cache: 'no-store'` so a cached copy can never mask a new deploy.
 *
 * Fails closed: any network error, non-OK response, non-JSON body (nginx's
 * `try_files $uri /index.html` returns index.html with a 200 when the file is
 * missing) or missing `version` field leaves `isOutdated` false.
 */

const VERSION_URL = `${process.env.PUBLIC_URL}/version.json`

interface AppVersionCheck {
  currentVersion: string
  latestVersion: string | null
  isOutdated: boolean
}

export const useAppVersionCheck = (): AppVersionCheck => {
  const [latestVersion, setLatestVersion] = useState<string | null>(null)
  const isMounted = useRef(true)

  const check = useCallback(async () => {
    try {
      const response = await fetch(`${VERSION_URL}?t=${Date.now()}`, {cache: 'no-store'})
      if (!response.ok) return

      // Not response.json() — a missing file is served as index.html with a
      // 200, and we want that to be a silent no-op rather than a thrown error.
      const body = await response.text()
      const parsed = JSON.parse(body)

      if (isMounted.current && typeof parsed?.version === 'string' && parsed.version) {
        setLatestVersion(parsed.version)
      }
    } catch {
      // Offline, blocked, or non-JSON body — never surface a false banner.
    }
  }, [])

  useEffect(() => {
    isMounted.current = true
    check()

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        check()
      }
    }

    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => {
      isMounted.current = false
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [check])

  return {
    currentVersion: APP_VERSION,
    latestVersion,
    isOutdated: !!latestVersion && latestVersion !== APP_VERSION,
  }
}

export default useAppVersionCheck
